import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useDispatch, useSelector } from 'react-redux';
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products';
import { getActiveUser } from '../store/slices/user';
import ss_b from '../public/Pictures/speakers-bn.jpg'
import speakers_a from '../public/Pictures/speakers-an.jpg'
import { speakerRentalFallback } from '../content/seo/speakerRental';
import AudioClosingActionBand from '../components/design-system/marketing/AudioClosingActionBand';
import CatalogHelp from '../components/design-system/commerce/CatalogHelp';
import Container from '../components/design-system/foundations/Container';
import EquipmentGuide from '../components/design-system/marketing/EquipmentGuide';
import EventShowcase from '../components/design-system/marketing/EventShowcase';
import FAQGroup from '../components/design-system/marketing/FAQGroup';
import MediaFeature from '../components/design-system/marketing/MediaFeature';
import PageShell from '../components/design-system/foundations/PageShell';
import ProductCatalog from '../components/design-system/commerce/ProductCatalog';
import RelatedServices from '../components/design-system/marketing/RelatedServices';
import ReviewProof from '../components/design-system/marketing/ReviewProof';
import ServiceHero from '../components/design-system/marketing/ServiceHero';
import ServiceIntroduction from '../components/design-system/marketing/ServiceIntroduction';
import ServiceCategoryGrid from '../components/design-system/marketing/ServiceCategoryGrid';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag';
import QuotePop from '../components/DeferredQuotePopup';
import { SHOW_POPUP } from '../store/slices/ui';
import * as gtag from '../lib/gtag';
import { compactSeoPublicPayload } from '../utilities/seoPublicPayload';
import { compactCatalogProducts } from '../utilities/catalogPublicPayload';
import { compactPublicEvents } from '../utilities/eventPublicPayload';
import { getAudioFamilyBreadcrumbs, toBreadcrumbJsonLdItems, toHeroBreadcrumbs } from '../utilities/audioFamilyBreadcrumbs';
import { buildSpeakerRentalCatalogOffer, isSpeakerRentalCatalogProduct, normalizeSpeakerFaq } from '../utilities/speakerRental';
import { requestPageData } from '../utilities/boundedPageData';

const SeoPageDrawer = dynamic(() => import('../components/SeoPageDrawer'), {
    ssr: false
})

const fallbackQuestions = [
    {
        question: 'Are there limitations on delivery and installation times during nights or holidays?',
        answer: 'We offer 24-hour delivery service for AV productions. Services scheduled from 8:00 PM to 8:00 AM may include an additional charge, which is shown before checkout.'
    },
    {
        question: 'If I only need the equipment for a few hours, do I still rent it for 24 hours?',
        answer: 'Yes. Speaker rental pricing is based on a 24-hour minimum rental period.'
    },
    {
        question: 'Do I need to pick up the speakers, or do you provide delivery?',
        answer: 'Select Choose Dates & Rent Online, then choose warehouse pickup or delivery and setup. We ask for your dates and location first so we can confirm the correct warehouse and show real availability.'
    },
    {
        question: 'Why is a deposit required, and can it be refunded if I cancel?',
        answer: 'The deposit reserves the equipment. A full refund is available when the rental is canceled at least 30 days before the scheduled date.'
    }
]

const sanitizeRevision = revision => {
    const sanitizeHtml = require('sanitize-html')
    const safeRevision = JSON.parse(JSON.stringify(revision))

    safeRevision.content = safeRevision.content || {}
    safeRevision.content.sections = (safeRevision.content.sections || []).map(section => ({
        ...section,
        body: sanitizeHtml(section.body || '', {
            allowedTags: [
                ...sanitizeHtml.defaults.allowedTags,
                'span'
            ],
            allowedAttributes: {
                ...sanitizeHtml.defaults.allowedAttributes,
                a: ['href', 'target', 'rel'],
                span: ['class'],
                p: ['class']
            },
            allowedSchemes: ['http', 'https', 'mailto', 'tel']
        })
    }))

    return safeRevision
}

const getSpeakerSeoData = async ({ preview, previewData }) => {
    const compactOptions = {
        customKeys: ['heroImagePosition', 'introductionImage', 'introductionImageAlt']
    }

    try {
        if (preview) {
            const response = await requestPageData({
              url: `${SERVER_IP}/seo/pages/preview`,
              label: 'SEO content',
              fallback: null,
              params: {
                path: speakerRentalFallback.page.path,
                token: previewData?.seoPreviewToken
              }
            })

            return compactSeoPublicPayload({
                ...response.data.data,
                revision: sanitizeRevision(response.data.data.revision)
            }, { ...compactOptions, source: 'draft-preview' })
        } else {
            const response = await requestPageData({
              url: `${SERVER_IP}/seo/pages/published`,
              label: 'SEO content',
              fallback: null,
              params: {
                path: speakerRentalFallback.page.path
              }
            })

            return compactSeoPublicPayload({
                ...response.data.data,
                revision: sanitizeRevision(response.data.data.revision)
            }, { ...compactOptions, source: 'published' })
        }
    } catch (error) {
        console.warn(`Using source fallback for ${speakerRentalFallback.page.path}: ${error.message}`)
    }

    return compactSeoPublicPayload({
        ...speakerRentalFallback,
        revision: sanitizeRevision(speakerRentalFallback.revision)
    }, { ...compactOptions, source: 'source-fallback' })
}

export const getStaticProps = wrapper.getStaticProps(
    async ({ store, preview, previewData }) => {

        const [landingResponse, subCategoryResponse, productResponse, faqResponse, seoData, reviewResponse] = await Promise.all([
            requestPageData({
              url: `${SERVER_IP}/user/static-landing`,
              fallback: {
                newsletterevents: [],
                total_events: null
              },
              label: `landing proof for ${speakerRentalFallback.page.path}`
            }),
            requestPageData({
              url: `${SERVER_IP}/subcategory/name/main-speakers`,
              params: {
                cat: 'sound'
              },
              fallback: {
                similar_categories: []
              },
              label: `related categories for ${speakerRentalFallback.page.path}`
            }),
            requestPageData({
              required: true,
              validateData: Array.isArray,
              url: `${SERVER_IP}/inventory/product`,
              params: {
                msr: true,
                cat: 'main_speakers'
              },
              label: `products for ${speakerRentalFallback.page.path}`
            }),
            requestPageData({
              url: `${SERVER_IP}/faq`,
              params: {
                category: 'Speaker'
              },
              fallback: fallbackQuestions,
              label: `FAQs for ${speakerRentalFallback.page.path}`
            }),
            getSpeakerSeoData({ preview, previewData }),
            requestPageData({
              url: `${SERVER_IP}/google-review/public`,
              params: {
                page: speakerRentalFallback.page.path,
                limit: 3
              },
              fallback: null,
              label: `reviews for ${speakerRentalFallback.page.path}`
            })
        ])
        const products = Array.isArray(productResponse.data) ? productResponse.data : []
        store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })

        store.dispatch({ type: productsSetFilters.type, payload: { static: true } })
        //    if(res.data)  store.dispatch({ type: packagesReceived.type, payload: res.data })

        const { newsletterevents = [], total_events = null } = landingResponse.data || {}
        const usedFallback = [landingResponse, subCategoryResponse, productResponse, faqResponse, reviewResponse]
            .some(response => response.usedFallback)
            || seoData?.source === 'source-fallback'

        return {
            props: {
                newsletterevents: compactPublicEvents(newsletterevents),
                total_events,
                subCate: subCategoryResponse.data,
                faq: faqResponse.data,
                seoData,
                reviewData: reviewResponse.data,
                isPreview: Boolean(preview)
            },
            revalidate: usedFallback ? 60 : 3600
        }
    }
);

const StoredHtml = ({ body, className }) => (
    <div
        className={className}
        dangerouslySetInnerHTML={{
            __html: String(body || '')
                .replace(/&amp;apos;/g, "'")
                .replace(/&apos;/g, "'")
        }}
    />
)

const clarifyAvailabilityCopy = value => String(value || '')
    .replace(/24\/7 availability\.?/gi, 'Choose dates to check live availability and rent online.')

const decodePlainText = value => String(value || '')
    .replace(/&amp;apos;|&apos;|&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')

const SpeakerRental = ({ newsletterevents, total_events, subCate, faq, seoData, reviewData, isPreview }) => {
    const products = useSelector(getFilteredProducts)
    const dispatch = useDispatch()
    const user = useSelector(getActiveUser)
    const page = seoData?.page || speakerRentalFallback.page
    const revision = seoData?.revision || speakerRentalFallback.revision
    const seo = revision.seo || speakerRentalFallback.revision.seo
    const seoDescription = clarifyAvailabilityCopy(seo.description)
    const content = revision.content || speakerRentalFallback.revision.content
    const sections = Object.fromEntries((content.sections || []).map(section => [section.key, section]))
    const section = key => sections[key] || speakerRentalFallback.revision.content.sections.find(item => item.key === key) || {}
    const breadcrumbs = getAudioFamilyBreadcrumbs('/speaker-rental')
    const heroImage = {
        src: content.hero?.picture || content.hero?.image || '/Pictures/categories-v2/speaker-rental-hero-v2.jpg',
        position: content.custom?.heroImagePosition || 'center'
    }
    const openGraph = {
        type: 'website',
        url: seo.openGraph?.url || seo.canonicalUrl || page.canonicalUrl,
        title: seo.openGraph?.title || seo.title,
        description: clarifyAvailabilityCopy(seo.openGraph?.description || seo.description),
        images: seo.openGraph?.images || speakerRentalFallback.revision.seo.openGraph.images,
        site_name: 'Miami Sound Rental'
    }
    const heroSteps = [
        { icon: 'search', title: 'Choose your gear', description: 'Compare speakers and bundles.' },
        { icon: 'calendar', title: 'Select dates', description: 'Tell us when you need it.' },
        { icon: 'truck', title: 'Delivery & setup', description: 'Pickup or professional setup.' },
        { icon: 'success', title: 'Event success', description: 'Clear sound with local support.' }
    ]
    const openQuote = () => {
        gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
        dispatch(SHOW_POPUP({
            key: 'quote',
            size: 'medium',

            body: <QuotePop />
        }))
    }
    const trackCall = () => gtag.event({
        action: 'booking_assistance_call_CTA',
        label: 'leads',
        value: 2,
        category: 'orders'
    })
    const primaryHeroAction = {
        label: 'Browse Speakers',
        href: '#catalog',
        icon: FiShoppingBag,
        conversionKey: 'speaker-rental.hero.browse-speakers',
        channel: 'rental',
        salesPath: 'self_service',
        onClick: () => gtag.event({
            action: 'catalog_click',
            label: 'leads',
            value: 2,
            category: 'orders'
        })
    }
    const secondaryHeroAction = {
        label: 'Talk to an Expert',
        icon: FiMessageCircle,
        conversionKey: 'speaker-rental.hero.talk-to-expert',
        channel: 'quote',
        salesPath: 'managed',
        onClick: openQuote
    }
    
    const faqQuestions = normalizeSpeakerFaq(faq?.length ? faq : fallbackQuestions)
    const speakerProducts = products.filter(isSpeakerRentalCatalogProduct)
    const speakerUseCases = [
        {
            eyebrow: 'Speech and presentations',
            title: section('use-case-speech').heading,
            summary: section('use-case-speech').subheading,
            body: <StoredHtml body={section('use-case-speech').body} />,
            image: speakers_a,
            imageAlt: 'Powered loudspeaker prepared for speech and presentation audio',
            href: '/sound/microphone-and-speaker',
            actionLabel: 'View Speech Packages'
        },
        {
            eyebrow: 'Music and celebrations',
            title: section('use-case-party').heading,
            summary: section('use-case-party').subheading,
            body: <StoredHtml body={section('use-case-party').body} />,
            image: { src: content.custom?.introductionImage || '/Pictures/categories-v2/speaker-rental-introduction-v2.webp' },
            imageAlt: 'DJ setup with powered speakers and subwoofers',
            href: '/sound/subwoofer',
            actionLabel: 'Compare Subwoofers'
        },
        {
            eyebrow: 'Planned coverage',
            title: section('use-case-outdoor').heading,
            summary: section('use-case-outdoor').subheading,
            body: <StoredHtml body={section('use-case-outdoor').body} />,
            image: ss_b,
            imageAlt: 'Professional event speakers arranged for broader audience coverage',
            href: '/sound-system-rental',
            actionLabel: 'Plan a Complete System'
        }
    ]
    const canonical = seo.canonicalUrl || page.canonicalUrl
    const serviceSchema = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${canonical}#service`,
        name: 'Speaker Rental',
        serviceType: 'Powered speaker and straightforward speaker bundle rental',
        url: canonical,
        description: seoDescription,
        provider: { '@id': 'https://miamisoundrental.com/' },
        areaServed: [
            { '@type': 'City', name: 'Miami' },
            { '@type': 'City', name: 'Fort Lauderdale' },
            { '@type': 'AdministrativeArea', name: 'South Florida' }
        ],
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Speaker rental options',
            itemListElement: speakerProducts.slice(0, 12).map(buildSpeakerRentalCatalogOffer)
        }
    }

    return <>
         <BreadcrumbJsonLd
        itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)}
      />
        <NextSeo
            title={seo.title}
            description={seoDescription}
            canonical={seo.canonicalUrl || page.canonicalUrl}
            noindex={seo.robots?.index === false}
            nofollow={seo.robots?.follow === false}
            twitter={{
                handle: '@miamisoundrent1',
                site: '@miamisoundrent1',
                cardType: seo.twitter?.cardType || 'summary_large_image',
            }}
            openGraph={openGraph}
            additionalMetaTags={[{
                name: 'keywords',
                content: [seo.primaryKeyword, ...(seo.secondaryKeywords || [])].filter(Boolean).join(', ')
            }]}
        />
        <Head>
            <script
                key='speaker-rental-service-schema'
                type='application/ld+json'
                dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
            />
        </Head>
        <PageShell>
            <ServiceHero
                breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
                eyebrow='Speaker rental · Miami & Fort Lauderdale'
                title={content.h1 || content.hero?.title}
                description={content.hero?.paragraph}
                image={heroImage}
                imageAlt={content.hero?.imageAlt || 'Powered speakers supporting a professional event presentation'}
                primaryAction={primaryHeroAction}
                secondaryAction={secondaryHeroAction}
                steps={heroSteps}
            />
            <ServiceIntroduction
                eyebrow='Miami Sound Rental'
                heading={section('rental-intro').heading}
                body={<StoredHtml body={section('rental-intro').body} />}
                guideHeading={section('selection-help').heading}
                guideSummary={decodePlainText(section('selection-help').subheading)}
                guideBody={<StoredHtml body={section('selection-help').body} />}
                image={{
                    src: content.custom?.introductionImage
                        || '/Pictures/categories-v2/speaker-rental-introduction-v2.webp'
                }}
                imageAlt={content.custom?.introductionImageAlt
                    || 'DJ setup flanked by professional powered speakers and subwoofers'}
                signals={[
                    'Individual powered speakers and speaker pairs with live availability',
                    'Pickup, delivery, and setup options shown before checkout',
                    'Expert escalation for subwoofers, multiple zones, and complete PA systems'
                ]}
            />
            <ProductCatalog
                carousel={{
                    name: 'Speaker Rentals',
                    url: 'https://miamisoundrental.com/speaker-rental',
                    description: 'Powered speakers and straightforward speaker bundles for rent'
                }}
                filters={[
                    ['Powered Speakers', 'cabinet'],
                    ['Portable Column Speakers', 'pla'],
                    ['Speaker + Bass Bundles', 'subtop']
                ]}
                sortOptions={['Wattage', 'Event Size', 'Sound Quality']}
                listName='Speaker Catalog'
                anchorId='catalog'
                eyebrow='Compare speaker options'
                products={speakerProducts}
            />
            <Container>
                <CatalogHelp onQuote={openQuote} onCall={trackCall} />
            </Container>

            <RelatedServices
                eyebrow='Choose the correct audio path'
                heading='Frequently rented together'
                description='Keep individual speakers here, or move directly to the specialist page when the event needs a microphone package, added bass, a complete PA, or engineered coverage.'
                subCategory={subCate}
                additionalServices={[
                    {
                        h1: 'Microphone & Speaker Bundles',
                        url: '/sound/microphone-and-speaker',
                        miniText: 'Compare ready-made speech and presentation packages with compatible microphones, speakers, stands, and cables.'
                    },
                    {
                        h1: 'Complete Sound System & PA Rental',
                        url: '/sound-system-rental',
                        miniText: 'Plan several speakers, multiple zones, larger audiences, live programs, delivery, setup, and technical support.'
                    },
                    {
                        h1: 'Subwoofer Rental',
                        url: '/sound/subwoofer',
                        miniText: 'Add dedicated low-frequency support for DJs, dance music, and bass-heavy programs.'
                    },
                    {
                        h1: 'Microphone Rental',
                        url: '/microphone-rental',
                        miniText: 'Compare wired, wireless, headset, lavalier, and conference microphone options.'
                    }
                ]}
            />

            <MediaFeature
                eyebrow='Speaker rental guide'
                heading={section('speaker-guide').heading}
                body={<StoredHtml body={section('speaker-guide').body} />}
                secondaryHeading={section('connectivity').heading}
                secondaryBody={<StoredHtml body={section('connectivity').body} />}
                image={{ src: '/Pictures/Product/pro-audio-rental-miami.jpg' }}
                imageAlt='Professional event speakers supporting a live band outdoors'
                reverse
            />

            <EquipmentGuide title='Speaker Rental by Event Type' items={speakerUseCases} />
            <FAQGroup questions={faqQuestions} />
            <ServiceCategoryGrid />
            <EventShowcase
                filter={['party', 'corporate', 'conference', 'concert', 'private', 'presentation']}
                events={newsletterevents}
                totalEvents={total_events}
            />
            <ReviewProof
                filter={['party', 'speaker']}
                heading='Recent feedback about speakers and sound'
                initialData={reviewData}
                pagePath={page.path || '/speaker-rental'}
            />
            <AudioClosingActionBand
                pagePath='/speaker-rental'
                primaryAction={primaryHeroAction}
                secondaryAction={secondaryHeroAction}
            />
        </PageShell>
        {user?.email === 'pedro@drynwet.com' && user?.employee && <SeoPageDrawer
            pagePath={page.path || '/speaker-rental'}
            initialSeoData={seoData}
            isPreview={isPreview}
        />}
    </>
}

export default SpeakerRental
